import { Modal } from 'almanac-ui/Modal'
import { Button } from 'blaze-ui/Button'
import { useState } from 'react'
import { FontRole, Font } from 'types'
import { FontWeightType } from 'components/VisualStudioPlayground/VisualEditor/Types'
import { FONT_WEIGHTS } from 'components/VisualStudioPlayground/Shapes/TextBoxShape/constants'
import { ComboBox } from 'components/ComboBox/ComboBox'
import { Item } from '@react-stately/collections'
import { useRootStore } from 'stores/RootStore'
import { useFonts } from 'hooks/queries/workspaces/useFonts'
import { FontFamilyComboBox } from 'components/VisualStudioPlayground/FontFamily/FontFamilyComboBox'
import Styles from './CustomizeFontModal.module.scss'
import { useModals } from 'almanac-ui/Modal'
import { useSanitiseFontConfig } from 'components/VisualStudioPlayground/VisualEditor/hooks/useSanitiseFontConfig'
import { observer } from 'mobx-react-lite'

interface Props {
  onSave: (role: FontRole, family: string, weight: FontWeightType, style: string) => void
  role: FontRole
  initialFontFamily: string
  initialFontWeight: FontWeightType
  initialFontStyle: string
}

const CustomFontWeight = observer(
  ({
    value,
    onChange,
    fontFamily,
  }: {
    value: FontWeightType
    onChange: (weight: FontWeightType) => void
    fontFamily: string
  }) => {
    const { fonts } = useRootStore()
    const font = fonts.get(fontFamily)
    if (!font) return null
    return (
      <ComboBox
        aria-label="Font weight"
        size="md"
        wrapperClassName={Styles.comboboxWrapper}
        contentsClassName={Styles.comboboxContents}
        inputClassName={Styles.comboboxInput}
        inputStyle={{ fontFamily: `'${fontFamily}'`, fontWeight: value }}
        __UNSAFE_popoverStyle={{ width: 200 }}
        menuTrigger="focus"
        onSelectionChange={(newWeight) => {
          if (!newWeight) return
          onChange(parseInt(newWeight as string, 10) as FontWeightType)
        }}
        selectedKey={value.toString()}
        inputValue={FONT_WEIGHTS[value]}
      >
        {font.weights.map((weight) => {
          const textValue = FONT_WEIGHTS[weight]
          return (
            <Item key={weight.toString()} textValue={textValue}>
              <span style={{ fontWeight: weight, fontFamily: `'${fontFamily}'` }}>{textValue}</span>
            </Item>
          )
        })}
      </ComboBox>
    )
  },
)

// Custom FontFamily component that doesn't depend on editor
const CustomFontFamily = ({
  value,
  onChange,
  fonts = [],
  brandKitFontFamilies = [],
}: {
  value: string
  onChange: (family: string, weights: FontWeightType[]) => void
  fonts?: Font[]
  brandKitFontFamilies?: string[]
}) => {
  const [isSearching, setIsSearching] = useState(false)

  const baseProps = {
    'aria-label': 'Font family',
    size: 'md' as const,
    wrapperClassName: Styles.comboboxWrapper,
    contentsClassName: Styles.comboboxContents,
    inputClassName: Styles.comboboxInput,
    inputStyle: { fontFamily: `'${value}'` },
    popoverClassName: Styles.comboboxPopover,
    __UNSAFE_popoverStyle: { width: 300 },
  }

  if (!isSearching) {
    return (
      <ComboBox {...baseProps} onFocus={() => setIsSearching(true)} inputValue={value}>
        <Item>
          <span style={{ fontFamily: `'${value}'` }}>{value}</span>
        </Item>
      </ComboBox>
    )
  }

  return (
    <FontFamilyComboBox
      {...baseProps}
      autoFocus
      placeholder="Search fonts..."
      menuTrigger="focus"
      onBlur={() => setIsSearching(false)}
      onSelectionChange={(newFamily, weights) => {
        if (!newFamily) return
        onChange(newFamily as string, weights)
        setIsSearching(false)
      }}
      selectedKey={value}
      defaultInputValue=""
      fonts={fonts}
      popoverMaxHeight={500}
      brandKitFontFamilies={brandKitFontFamilies}
    />
  )
}

const CustomizeFontModal = ({ onSave, role, initialFontFamily, initialFontWeight, initialFontStyle }: Props) => {
  const [fontFamily, setFontFamily] = useState(initialFontFamily)
  const [fontWeight, setFontWeight] = useState(initialFontWeight)
  const [fontStyle, setFontStyle] = useState(initialFontStyle)
  const { closeModal } = useModals()
  const sanitiseFontConfig = useSanitiseFontConfig()

  const { data: fontsData } = useFonts({ cacheTime: 1000 * 60 * 5 })

  const handleSave = () => {
    onSave(role, fontFamily, fontWeight, fontStyle)
    closeModal()
  }

  const handleCancel = () => {
    // Reset to initial values
    setFontFamily(initialFontFamily)
    setFontWeight(initialFontWeight)
    setFontStyle(initialFontStyle)
    closeModal()
  }

  return (
    <Modal.Root size="sm">
      <Modal.Header title="Customize Font" />
      <Modal.Content>
        <div className={Styles.fieldsContainer}>
          <div className={Styles.fieldGroup}>
            <CustomFontFamily
              value={fontFamily}
              onChange={(family) => {
                const sanitisedConfig = sanitiseFontConfig({ fontFamily: family, weight: fontWeight, italic: false })

                setFontFamily(sanitisedConfig.fontFamily)
                setFontWeight(sanitisedConfig.weight)
                setFontStyle(sanitisedConfig.italic ? 'italic' : 'normal')
              }}
              fonts={fontsData?.fonts}
              brandKitFontFamilies={[]}
            />
          </div>

          <div className={Styles.fieldGroup}>
            <CustomFontWeight value={fontWeight} onChange={setFontWeight} fontFamily={fontFamily} />
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Button variant="tertiary" onPress={handleCancel} size="lg">
            Cancel
          </Button>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Button variant="primary" onPress={handleSave} size="lg">
            Confirm
          </Button>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}

export default CustomizeFontModal
